/** Time zone maths using only Intl: handles daylight-saving gaps and repeated hours. */

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(zone: string) {
  let f = fmtCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
    fmtCache.set(zone, f);
  }
  return f;
}

/** UTC offset of `zone` at the instant `utcMs`, in minutes (east positive). */
export function offsetMinutes(utcMs: number, zone: string): number {
  const parts = fmt(zone).formatToParts(new Date(Math.floor(utcMs / 1000) * 1000));
  const g = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour") % 24, g("minute"), g("second"));
  return Math.round((asUtc - Math.floor(utcMs / 1000) * 1000) / 60000);
}

export interface Zoned { utc: number; kind: "ok" | "gap" | "ambiguous" }

/** Turn a wall-clock time in `zone` into a real instant. */
export function zonedToUtc(y: number, mo: number, d: number, h: number, mi: number, zone: string): Zoned {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const DAY = 86400000;
  const offs = Array.from(new Set([offsetMinutes(guess - DAY, zone), offsetMinutes(guess + DAY, zone)]));
  const valid = offs.map((o) => guess - o * 60000).filter((u) => offsetMinutes(u, zone) === offs.find((o) => guess - o * 60000 === u));
  const uniq = Array.from(new Set(valid)).sort((a, b) => a - b);
  if (uniq.length === 1) return { utc: uniq[0], kind: "ok" };
  if (uniq.length > 1) return { utc: uniq[0], kind: "ambiguous" };
  // Clocks skipped this time: read it with the offset from before the jump (lands just after the gap).
  return { utc: guess - offsets(offs) * 60000, kind: "gap" };
}
const offsets = (offs: number[]) => Math.min(...offs);

/** "YYYY-MM-DDTHH:mm" for the current moment as shown in `zone`. */
export function nowInZone(zone: string, now = Date.now()): string {
  const parts = fmt(zone).formatToParts(new Date(now));
  const g = (t: string) => String(parts.find((p) => p.type === t)?.value).padStart(2, "0");
  return `${g("year").padStart(4, "0")}-${g("month")}-${g("day")}T${String(Number(g("hour")) % 24).padStart(2, "0")}:${g("minute")}`;
}

export function offsetLabel(utcMs: number, zone: string): string {
  const o = offsetMinutes(utcMs, zone);
  const sign = o < 0 ? "−" : "+";
  const a = Math.abs(o);
  return `UTC${sign}${Math.floor(a / 60)}${a % 60 ? ":" + String(a % 60).padStart(2, "0") : ""}`;
}

/** Old names that browsers still report, mapped to the current ones. Both are accepted when searching. */
export const LEGACY_ZONES: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata", "Asia/Katmandu": "Asia/Kathmandu", "Europe/Kiev": "Europe/Kyiv", "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon", "Asia/Dacca": "Asia/Dhaka", "America/Buenos_Aires": "America/Argentina/Buenos_Aires", "Asia/Thimbu": "Asia/Thimphu",
  "Pacific/Truk": "Pacific/Chuuk", "Atlantic/Faeroe": "Atlantic/Faroe", "Asia/Ulan_Bator": "Asia/Ulaanbaatar",
};
const MODERN_TO_LEGACY: Record<string, string[]> = {};
for (const [old, now] of Object.entries(LEGACY_ZONES)) (MODERN_TO_LEGACY[now] ||= []).push(old);

/** The current name for a zone (Asia/Kolkata for Asia/Calcutta, and so on). */
export function modernZone(z: string): string {
  return LEGACY_ZONES[z] ?? z;
}

function zoneWorks(z: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: z }); return true; } catch { return false; }
}

export function allZones(): string[] {
  let base: string[] = [];
  try {
    const list = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone");
    if (list?.length) base = list;
  } catch { /* fall through */ }
  if (!base.length) base = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Asia/Kolkata", "Australia/Sydney"];
  const out = new Set<string>(["UTC"]);
  for (const z of base) out.add(modernZone(z));
  // Make sure the modern names are offered even when this browser only lists the old ones.
  for (const now of Object.values(LEGACY_ZONES)) if (!out.has(now) && zoneWorks(now)) out.add(now);
  const [utc, ...rest] = Array.from(out);
  return [utc, ...rest.sort()];
}

export function browserZone(): string {
  try { return modernZone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"); } catch { return "UTC"; }
}

/** Common abbreviations people type in the search box. Some are shared by several places. */
export const ZONE_ALIASES: Record<string, string[]> = {
  est: ["America/New_York"], edt: ["America/New_York"], cst: ["America/Chicago", "Asia/Shanghai"], cdt: ["America/Chicago"], mst: ["America/Denver", "America/Phoenix"], mdt: ["America/Denver"],
  pst: ["America/Los_Angeles"], pdt: ["America/Los_Angeles"], akst: ["America/Anchorage"], hst: ["Pacific/Honolulu"],
  ist: ["Asia/Kolkata", "Asia/Jerusalem", "Europe/Dublin"], gmt: ["Europe/London", "UTC"], bst: ["Europe/London"], wet: ["Europe/Lisbon"],
  cet: ["Europe/Paris", "Europe/Berlin"], cest: ["Europe/Paris", "Europe/Berlin"], eet: ["Europe/Athens", "Europe/Helsinki"], eest: ["Europe/Athens"],
  jst: ["Asia/Tokyo"], kst: ["Asia/Seoul"], aest: ["Australia/Sydney"], aedt: ["Australia/Sydney"], acst: ["Australia/Adelaide"], awst: ["Australia/Perth"],
  nzst: ["Pacific/Auckland"], nzdt: ["Pacific/Auckland"], sgt: ["Asia/Singapore"], hkt: ["Asia/Hong_Kong"], pkt: ["Asia/Karachi"], npt: ["Asia/Kathmandu"],
  msk: ["Europe/Moscow"], sast: ["Africa/Johannesburg"], cat: ["Africa/Maputo"], eat: ["Africa/Nairobi"], wat: ["Africa/Lagos"], gst: ["Asia/Dubai"], utc: ["UTC"], ict: ["Asia/Bangkok"],
  wib: ["Asia/Jakarta"], pht: ["Asia/Manila"], brt: ["America/Sao_Paulo"], art: ["America/Argentina/Buenos_Aires"],
};

/** City and country words for well-known zones, so "Mumbai" or "India" finds Asia/Kolkata. */
const ZONE_WORDS: Record<string, string> = {
  "Asia/Kolkata": "india mumbai bombay delhi new delhi bangalore bengaluru chennai madras hyderabad pune ahmedabad calcutta indian standard time",
  "Asia/Kathmandu": "nepal katmandu", "Asia/Dhaka": "bangladesh dacca", "Asia/Colombo": "sri lanka", "Asia/Karachi": "pakistan lahore islamabad",
  "Asia/Dubai": "uae united arab emirates abu dhabi gulf", "Asia/Riyadh": "saudi arabia", "Asia/Tehran": "iran", "Asia/Jerusalem": "israel tel aviv",
  "Asia/Singapore": "singapore", "Asia/Hong_Kong": "hong kong china", "Asia/Shanghai": "china beijing shanghai chengdu shenzhen", "Asia/Tokyo": "japan tokyo osaka",
  "Asia/Seoul": "korea south korea", "Asia/Bangkok": "thailand", "Asia/Jakarta": "indonesia", "Asia/Manila": "philippines", "Asia/Kuala_Lumpur": "malaysia",
  "Asia/Ho_Chi_Minh": "vietnam hanoi saigon", "Asia/Taipei": "taiwan", "Asia/Yangon": "myanmar burma rangoon",
  "Europe/London": "united kingdom uk britain england scotland wales gmt bst", "Europe/Dublin": "ireland", "Europe/Paris": "france", "Europe/Berlin": "germany munich frankfurt",
  "Europe/Madrid": "spain barcelona", "Europe/Rome": "italy milan", "Europe/Amsterdam": "netherlands holland", "Europe/Zurich": "switzerland", "Europe/Moscow": "russia",
  "Europe/Istanbul": "turkey", "Europe/Kyiv": "ukraine kiev", "Europe/Lisbon": "portugal", "Europe/Athens": "greece", "Europe/Stockholm": "sweden", "Europe/Warsaw": "poland",
  "America/New_York": "usa united states eastern new york washington boston miami atlanta", "America/Chicago": "usa united states central texas dallas houston",
  "America/Denver": "usa united states mountain colorado", "America/Phoenix": "usa arizona mountain", "America/Los_Angeles": "usa united states pacific california san francisco seattle",
  "America/Anchorage": "usa alaska", "Pacific/Honolulu": "usa hawaii", "America/Toronto": "canada ontario eastern", "America/Vancouver": "canada british columbia pacific",
  "America/Mexico_City": "mexico", "America/Sao_Paulo": "brazil", "America/Argentina/Buenos_Aires": "argentina", "America/Bogota": "colombia", "America/Lima": "peru", "America/Santiago": "chile",
  "Africa/Johannesburg": "south africa cape town", "Africa/Lagos": "nigeria", "Africa/Cairo": "egypt", "Africa/Nairobi": "kenya", "Africa/Casablanca": "morocco",
  "Australia/Sydney": "australia melbourne canberra", "Australia/Perth": "australia", "Australia/Brisbane": "australia", "Australia/Adelaide": "australia",
  "Pacific/Auckland": "new zealand nz wellington", "Pacific/Pago_Pago": "samoa american samoa", "Pacific/Kiritimati": "kiribati line islands", "UTC": "utc gmt zulu coordinated universal time",
};

const norm = (t: string) => t.toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ").trim();

/** Zones matching the search text by name, city, country or abbreviation. The current zone is always kept in the list. */
export function searchZones(zones: readonly string[], query: string, current?: string): string[] {
  const q = norm(query);
  if (!q) return [...zones];
  const alias = ZONE_ALIASES[q] ?? [];
  const words = q.split(" ");
  const hits = zones.filter((z) => {
    const hay = norm([z, ZONE_WORDS[z] ?? "", ...(MODERN_TO_LEGACY[z] ?? [])].join(" "));
    return words.every((w) => hay.includes(w));
  });
  const aliasHits = alias.filter((z) => zones.includes(z));
  const out = Array.from(new Set([...aliasHits, ...hits]));
  if (current && !out.includes(current)) out.push(current);
  return out;
}

export function prettyZone(z: string): string {
  return z.replace(/_/g, " ");
}
